<script setup lang="ts">
import type { StickerFill, StickerTilt } from './SfgSticker.vue'

const props = withDefaults(defineProps<{
  bgColor?: StickerFill
  bodyColor?: StickerFill
  disk?: boolean
  flameColor?: StickerFill
  /** Accepts a number (px) or any CSS length string, including clamp(). */
  size?: number | string
  tilt?: StickerTilt
}>(), {
  bgColor: 'red',
  bodyColor: 'red',
  disk: true,
  flameColor: 'yellow',
  size: 140,
  tilt: 1,
})

const bgVar = computed(() => `var(--color-brand-${props.bgColor})`)
const bodyVar = computed(() => `var(--color-brand-${props.bodyColor})`)
const flameVar = computed(() => `var(--color-brand-${props.flameColor})`)
const sizeCss = computed(() => (typeof props.size === 'number' ? `${props.size}px` : props.size))

// Flame paths on viewBox "-60 -20 782.216 1080" — aspect ratio ~0.725 (w:h)
const BODY_PATH = 'M47.827,589.343c10.03-17.093,22.187-32.952,34.907-48.192-13.9,16.654,2.628,73.429,7.846,91.523,8.645,29.976,16.529,56.739,37.393,81.306-17.85-21.018-8-79.03-1.492-102.554,9.221-33.308,24.719-61.2,45.839-88.5,39.716-51.343,90.69-95,115.428-156.513a337.764,337.764,0,0,0,24.285-129.234,321.535,321.535,0,0,0-7.382-65.5c-2.442-11.107-8.982-53.255-19.448-58.943a336.781,336.781,0,0,1,94.461,76.328c47.137,54.909,75.5,124.158,89.5,195.157S483.437,528.253,479.332,600.5A410.791,410.791,0,0,0,540.979,484.61c28.717,57.7,46.7,121.179,48.562,185.605,2.154,74.457-17.949,150.131-60.783,211.072-34.714,49.389-85.99,86.923-142.191,108.347-54.985,20.96-138.374,6.884-192.183-13.454C96.657,939.242,28.831,840.036,18.818,736.042,14.511,691.318,20.052,645,39.659,604.574Q43.435,596.787,47.827,589.343Z'
const FLAME_PATH = 'M177.073,914.538c-4.4-4.03-8.552-8.166-12.429-12.364a209.2,209.2,0,0,1-35.725-53.056,199.366,199.366,0,0,1-11.484-30.867c-2.247-8.037-1.79-21.789-5.7-28.742,13.009,23.118,37.086,37.4,60.082,50.626,15.2,8.742,30.5,17.527,46.955,23.593-19.931-30.034-33.269-72.342-28.7-108.562,12.121-96,93.44-159.753,137.594-240.636,23.5-43.053,40.161-90.5,43.066-139.461a594.024,594.024,0,0,1,7.029,275.741c20.6-36.151,38.742-74.061,49.014-114.379,11.7,42.08,10.516,89.37,6.083,132.538a290.17,290.17,0,0,1-41.215,121.866A513.563,513.563,0,0,0,526.773,643.783a416.387,416.387,0,0,1-14.74,153.069c-11.016,37.987-28.188,75.173-54.49,105.066-25.7,29.212-57.825,56.006-96.59,64.356-26.022,5.606-53.065,4.648-79.451,1.122C244.256,962.421,205.633,940.724,177.073,914.538Z'
</script>

<template>
  <div
    v-if="disk"
    :style="{
      '--flame-size': sizeCss,
      'width': 'var(--flame-size)',
      'height': 'var(--flame-size)',
      'background': bgVar,
      'transform': `rotate(var(--sticker-rotate-${props.tilt}))`,
      'boxShadow': 'var(--shadow-sticker)',
    }"
    class="inline-flex items-center justify-center rounded-full"
  >
    <svg
      :style="{ width: 'calc(var(--flame-size) * 0.5)', height: 'calc(var(--flame-size) * 0.68)' }"
      viewBox="-60 -20 782.216 1080"
    >
      <g
        stroke="var(--color-brand-ink)"
        stroke-linejoin="round"
        stroke-linecap="round"
      >
        <path
          :d="BODY_PATH"
          :fill="bodyVar"
          stroke-width="18"
        />
        <path
          :d="FLAME_PATH"
          :fill="flameVar"
          stroke-width="14"
        />
      </g>
    </svg>
  </div>
  <svg
    v-else
    :style="{
      '--flame-size': sizeCss,
      'width': 'calc(var(--flame-size) * 0.725)',
      'height': 'var(--flame-size)',
      'transform': `rotate(var(--sticker-rotate-${props.tilt}))`,
      'filter': 'drop-shadow(0 2px 0 rgba(0, 0, 0, 0.85))',
    }"
    viewBox="-60 -20 782.216 1080"
  >
    <g
      stroke="var(--color-brand-ink)"
      stroke-linejoin="round"
      stroke-linecap="round"
    >
      <path
        :d="BODY_PATH"
        :fill="bodyVar"
        stroke-width="18"
      />
      <path
        :d="FLAME_PATH"
        :fill="flameVar"
        stroke-width="14"
      />
    </g>
  </svg>
</template>
